import React, { useState, useEffect } from 'react';
import './App.css';
import './components/SharedStyles.css';
import Login from './components/Login';
import Signup from './components/Signup';
import Verification from './components/Verification';
import Dashboard from './components/Dashboard';
import GuestPage from './components/GuestPage';
import authService from './services/authService';

function App() {
  const [currentScreen, setCurrentScreen] = useState('login');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userRole, setUserRole] = useState('guest');
  const [userData, setUserData] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load user session from localStorage on app mount
  useEffect(() => {
    const savedUser = localStorage.getItem('insafUser');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setPhoneNumber(user.phoneNumber);
        setUserRole(user.role);
        setUserData(user.userData || null);
        setToken(user.token || null);
        
        // Restore to appropriate screen based on role
        if (user.role === 'lawyer') {
          setCurrentScreen('dashboard');
          window.location.hash = '/dashboard';
        } else {
          setCurrentScreen('guest');
          window.location.hash = '/guest';
        }
      } catch (error) {
        console.error('Error loading saved session:', error);
        localStorage.removeItem('insafUser');
      }
    }
    setIsLoaded(true);
  }, []);

  // Listen for URL changes (back/forward buttons)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove '#'
      
      if (hash === '/login' || hash === '') {
        setCurrentScreen('login');
      } else if (hash === '/signup') {
        setCurrentScreen('signup');
      } else if (hash === '/verification') {
        setCurrentScreen('verification');
      } else if (hash === '/dashboard') {
        // Only allow dashboard if user is logged in as lawyer
        const savedUser = localStorage.getItem('insafUser');
        if (savedUser) {
          try {
            const user = JSON.parse(savedUser);
            if (user.role === 'lawyer') {
              setCurrentScreen('dashboard');
            } else {
              window.location.hash = '/guest';
            }
          } catch (error) {
            window.location.hash = '/login';
          }
        } else {
          window.location.hash = '/login';
        }
      } else if (hash === '/guest') {
        // Only allow guest if user is logged in as guest
        const savedUser = localStorage.getItem('insafUser');
        if (savedUser) {
          try {
            const user = JSON.parse(savedUser);
            if (user.role !== 'lawyer') {
              setCurrentScreen('guest');
            } else {
              window.location.hash = '/dashboard';
            }
          } catch (error) {
            window.location.hash = '/login';
          }
        } else {
          window.location.hash = '/login';
        }
      } else {
        window.location.hash = '/login';
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleOTPSent = (phone) => {
    setPhoneNumber(phone);
    setCurrentScreen('verification');
    window.location.hash = '/verification';
  };

  const handleBackToLogin = () => {
    setCurrentScreen('login');
    setPhoneNumber('');
    window.location.hash = '/login';
  };

  const handleSignupClick = () => {
    setCurrentScreen('signup');
    window.location.hash = '/signup';
  };

  const handleSignupSuccess = (phone) => {
    setPhoneNumber(phone);
    setCurrentScreen('verification');
    window.location.hash = '/verification';
  };

  const handleVerificationSuccess = async (role, user, authToken) => {
    try {
      setUserRole(role);
      setUserData(user);
      setToken(authToken);
      
      // If we have a token, fetch the complete profile from getProfile API
      let completeUserData = user;
      if (authToken) {
        try {
          console.log('Fetching complete profile with token...');
          const profileResponse = await authService.getProfile(authToken);
          if (profileResponse.data || profileResponse.user) {
            completeUserData = profileResponse.data || profileResponse.user;
            console.log('Profile fetched successfully:', completeUserData);
          }
        } catch (profileError) {
          console.error('Profile fetch failed, using verification data:', profileError);
          // Continue with user data from verification response
        }
      }
      
      // Save user session to localStorage
      const sessionData = {
        phoneNumber,
        role,
        userData: completeUserData,
        token: authToken,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem('insafUser', JSON.stringify(sessionData));
      
      // Route based on user role and update URL
      if (role === 'lawyer') {
        setCurrentScreen('dashboard');
        window.location.hash = '/dashboard';
      } else {
        // Guests go to guest page
        setCurrentScreen('guest');
        window.location.hash = '/guest';
      }
    } catch (error) {
      console.error('Error in verification success:', error);
      // Still proceed with verification even if profile fetch fails
      setUserRole(role);
      setUserData(user);
      setToken(authToken);
      
      const sessionData = {
        phoneNumber,
        role,
        userData: user,
        token: authToken,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem('insafUser', JSON.stringify(sessionData));
      
      if (role === 'lawyer') {
        setCurrentScreen('dashboard');
        window.location.hash = '/dashboard';
      } else {
        setCurrentScreen('guest');
        window.location.hash = '/guest';
      }
    }
  };

  const handleLogout = () => {
    // Clear user session from localStorage
    localStorage.removeItem('insafUser');
    
    // Reset state
    setCurrentScreen('login');
    setPhoneNumber('');
    setUserRole('guest');
    setUserData(null);
    setToken(null);
    
    // Update URL
    window.location.hash = '/login';
  };

  return (
    <div className="App">
      {!isLoaded ? (
        <div className="app-loading">
          <div className="app-loading-inner">
            <div className="app-loading-icon">⚖️</div>
            <div className="app-loading-brand">INSAFAPP.COM</div>
            <div className="app-loading-spinner"></div>
          </div>
        </div>
      ) : currentScreen === 'login' ? (
        <Login onOTPSent={handleOTPSent} onSignupClick={handleSignupClick} />
      ) : currentScreen === 'signup' ? (
        <Signup onBack={handleBackToLogin} onSignupSuccess={handleSignupSuccess} />
      ) : currentScreen === 'verification' ? (
        <Verification phoneNumber={phoneNumber} onBack={handleBackToLogin} onVerificationSuccess={handleVerificationSuccess} />
      ) : currentScreen === 'dashboard' ? (
        <Dashboard userRole={userRole} phoneNumber={phoneNumber} userData={userData} token={token} onLogout={handleLogout} />
      ) : currentScreen === 'guest' ? (
        <GuestPage phoneNumber={phoneNumber} userData={userData} token={token} onLogout={handleLogout} />
      ) : null}
    </div>
  );
}

export default App;
